import { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import Alert from '@mui/material/Alert'
import Snackbar from '@mui/material/Snackbar'
import { urlBase64ToUint8Array } from '@common/helpers/helpers'
import { userRoleGroups } from '@common/interfaces/user'
import type { UserRole } from '@common/interfaces/user'
import { createStructuredSelector } from 'reselect'
import api from '../../api'
import config from '../../config'
import { isIosDevice } from '../../helpers/platform'
import { buildPushSubscriptionPayload } from '../../helpers/pushDeviceId'
import { role as roleSelector } from '../../redux/profile/selectors'
import Button from '../Common/Button'
import Icon from '../Common/Icon'
import IconButton from '../Common/IconButton'
import Text from '../Common/Text'
import styles from './EnableNotificationsBanner.module.scss'

const DISMISS_KEY = 'notifications_banner_dismissed'
const IOS_INSTALL_DISMISS_KEY = 'ios_install_banner_dismissed'

/** Running from the home screen rather than inside a browser tab. */
const isInstalledPwa = (): boolean =>
  window.matchMedia('(display-mode: standalone)').matches ||
  (window.navigator as Navigator & { standalone?: boolean }).standalone === true

/**
 * Which prompt, if any, this device should see.
 *
 * iOS 16.4+ delivers web push **only to a PWA installed to the home screen** —
 * in a Safari tab `pushManager.subscribe` fails no matter what the user taps. So
 * on iOS the honest first step is Add to Home Screen, not an Enable button that
 * cannot work. Everywhere else the permission prompt is the whole flow.
 */
type BannerMode = 'none' | 'enable' | 'iosInstall'

interface IProps {
  role?: UserRole
}

const EnableNotificationsBanner = ({ role }: IProps) => {
  const [mode, setMode] = useState<BannerMode>('none')
  const [isRequesting, setIsRequesting] = useState(false)
  const show = mode !== 'none'

  // Hide entirely in puppeteer snapshot mode so the banner doesn't end up
  // in exported PDFs. Read straight off `window.location` because this
  // component is mounted in `app.tsx`, above the React Router context.
  const isSnapshotMode =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('snapshot') === 'true'

  useEffect(() => {
    if (isSnapshotMode) {
      setMode('none')
      return
    }

    // Only internal users — clients don't get portal push.
    if (!role || !userRoleGroups.INTERNAL.includes(role)) {
      setMode('none')
      return
    }

    // iOS in a browser tab: no amount of tapping Enable will subscribe, so ask
    // for the install instead. Once installed this branch stops matching and the
    // ordinary permission prompt takes over.
    if (isIosDevice() && !isInstalledPwa()) {
      setMode(localStorage.getItem(IOS_INSTALL_DISMISS_KEY) ? 'none' : 'iosInstall')
      return
    }

    const canPrompt =
      'PushManager' in window &&
      Notification.permission === 'default' &&
      !localStorage.getItem(DISMISS_KEY)

    setMode(canPrompt ? 'enable' : 'none')
  }, [role, isSnapshotMode])

  const handleEnableNotifications = async () => {
    setIsRequesting(true)
    try {
      // Request permission from user
      const permission = await Notification.requestPermission()

      if (permission === 'granted') {
        // Subscribe to push notifications
        if ('serviceWorker' in navigator) {
          const registration = await navigator.serviceWorker.ready

          if (registration.pushManager) {
            const convertedVapidKey = urlBase64ToUint8Array(config.vapidPublicKey)

            const subscription = await registration.pushManager.subscribe({
              applicationServerKey: convertedVapidKey as unknown as ArrayBuffer,
              userVisibleOnly: true,
            })

            // Send subscription to server via REST API
            await api.pushSubscription.subscribe(buildPushSubscriptionPayload(subscription))
          }
        }

        // Hide banner after successful subscription
        setMode('none')
      } else {
        // User denied or dismissed permission - hide banner
        setMode('none')
        localStorage.setItem(DISMISS_KEY, 'true')
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to enable push notifications:', error)
    } finally {
      setIsRequesting(false)
    }
  }

  const handleDismiss = () => {
    localStorage.setItem(mode === 'iosInstall' ? IOS_INSTALL_DISMISS_KEY : DISMISS_KEY, 'true')
    setMode('none')
  }

  if (mode === 'iosInstall') {
    return (
      <Snackbar
        open={show}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        className={styles.snackbar}
      >
        <Alert
          severity="info"
          icon={<Icon name="bell" size="lg" />}
          data-cy="ios-install-banner"
          action={
            <IconButton
              title="Dismiss"
              size="small"
              onClick={handleDismiss}
              color="inherit"
              data-cy="ios-install-close"
            >
              <Icon name="x" size="sm" />
            </IconButton>
          }
          className={styles.alert}
        >
          <Text variant="title" component="strong">
            Add Atlas to your Home Screen
          </Text>
          <br />
          Tap Share, then &ldquo;Add to Home Screen&rdquo;. iOS only delivers Atlas notifications
          once it&rsquo;s installed &mdash; you&rsquo;ll get every category ticked under Mobile.
        </Alert>
      </Snackbar>
    )
  }

  return (
    <Snackbar
      open={show}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      className={styles.snackbar}
    >
      <Alert
        severity="info"
        icon={<Icon name="bell" size="lg" />}
        data-cy="enable-notifications-banner"
        action={
          <>
            <Button
              onClick={handleEnableNotifications}
              disabled={isRequesting}
              size="small"
              className={styles.enableButton}
            >
              {isRequesting ? 'Enabling...' : 'Enable'}
            </Button>
            <IconButton
              title="Dismiss"
              size="small"
              onClick={handleDismiss}
              color="inherit"
              data-cy="enable-notifications-close"
            >
              <Icon name="x" size="sm" />
            </IconButton>
          </>
        }
        className={styles.alert}
      >
        <Text variant="title" component="strong">
          Enable notifications
        </Text>
        <br />
        Stay updated with important alerts and activity notifications
      </Alert>
    </Snackbar>
  )
}

const selector = createStructuredSelector({
  role: roleSelector,
})

export default connect(selector)(EnableNotificationsBanner)
